import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarPlus, Check, MapPin, Sparkles, Ticket as TicketIcon } from "lucide-react";
import { useContent } from "../lib/content";
import { formatDate, money } from "../lib/api";

const parseStart = (dateISO, label) => {
  const m = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i.exec(label || "");
  let h = 18;
  let min = 0;
  if (m) {
    h = parseInt(m[1], 10) % 12;
    if (m[3].toLowerCase() === "pm") h += 12;
    min = parseInt(m[2] || "0", 10);
  }
  const d = new Date(`${dateISO}T00:00:00`);
  d.setHours(h, min, 0, 0);
  return d;
};

const stamp = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
const fmtTime = (d) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

export const TicketCard = ({ booking }) => {
  const { t } = useContent();
  const [calOpen, setCalOpen] = useState(false);
  const [prepareOpen, setPrepareOpen] = useState(false);

  const start = parseStart(booking.workshop_date, booking.timeslot_label);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const title = `${booking.workshop_title} at MAS Arts Studio`;
  const location = "MAS Arts Studio, 817 New York Ave, Sheboygan WI";
  const details = `Booking code ${booking.booking_code} — show this code at the door. Snacks & drinks included.`;

  const googleUrl =
    "https://calendar.google.com/calendar/render?action=TEMPLATE" +
    `&text=${encodeURIComponent(title)}` +
    `&dates=${stamp(start)}/${stamp(end)}` +
    `&details=${encodeURIComponent(details)}` +
    `&location=${encodeURIComponent(location)}`;

  const downloadIcs = () => {
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//MAS Arts Studio//Booking//EN",
      "BEGIN:VEVENT",
      `UID:${booking.booking_code}@masarts`,
      `DTSTAMP:${stamp(new Date())}`,
      `DTSTART:${stamp(start)}`,
      `DTEND:${stamp(end)}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${details}`,
      `LOCATION:${location}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mas-arts-${booking.booking_code}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const prepareItems = (t("prepare_items") || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div>
      <div className="grid gap-5 md:grid-cols-2">
        <div
          data-testid="ticket-details-card"
          className="rounded-[26px] overflow-hidden bg-white border border-black/[0.06] flex flex-col"
        >
          {booking.image && (
            <img src={booking.image} alt={booking.workshop_title} className="h-44 sm:h-52 w-full object-cover" />
          )}
          <div className="p-6 sm:p-7 flex flex-col flex-1">
            <h4 className="font-display font-extrabold text-2xl tracking-tight">{booking.workshop_title}</h4>
            <p className="text-sm font-bold mt-3">{formatDate(booking.workshop_date)}</p>
            {booking.timeslot_label && (
              <p className="text-sm font-medium text-neutral-500">
                {/[-–]/.test(booking.timeslot_label)
                  ? booking.timeslot_label
                  : `${booking.timeslot_label} – ${fmtTime(end)}`}
              </p>
            )}
            <p className="flex items-center gap-1.5 text-sm font-medium text-neutral-500 mt-3">
              <MapPin size={14} className="text-[#c63f3e] shrink-0" />
              817 New York Ave, Sheboygan, WI
            </p>
            <p className="text-sm font-medium text-neutral-500 mt-3">
              {booking.seats} {booking.tier_name || "regular"} ticket{booking.seats > 1 ? "s" : ""}
              {booking.total_cents > 0 ? ` · ${money(booking.total_cents)} paid` : ""}
            </p>
            <div className="mt-auto pt-6 flex flex-wrap items-center gap-2.5">
              <div className="relative">
                <button
                  data-testid="ticket-add-calendar"
                  onClick={() => setCalOpen((o) => !o)}
                  className="btn-primary inline-flex items-center gap-2 rounded-full px-5 py-3 text-xs font-bold"
                >
                  <CalendarPlus size={14} /> Add to calendar
                </button>
                <AnimatePresence>
                  {calOpen && (
                    <motion.div
                      data-testid="ticket-calendar-menu"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-0 bottom-full mb-2 w-60 rounded-2xl bg-white border border-black/[0.08] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] p-1.5 z-20"
                    >
                      <a
                        data-testid="ticket-google-calendar"
                        href={googleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setCalOpen(false)}
                        className="block rounded-xl px-4 py-2.5 text-xs font-bold hover:bg-[#eae4da]/60 transition-colors"
                      >
                        Google Calendar (Android & web)
                      </a>
                      <button
                        data-testid="ticket-apple-calendar"
                        onClick={() => { downloadIcs(); setCalOpen(false); }}
                        className="block w-full text-left rounded-xl px-4 py-2.5 text-xs font-bold hover:bg-[#eae4da]/60 transition-colors"
                      >
                        Apple Calendar (.ics download)
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {prepareItems.length > 0 && (
                <button
                  data-testid="ticket-how-to-prepare"
                  onClick={() => setPrepareOpen((o) => !o)}
                  className="inline-flex items-center gap-2 rounded-full border border-neutral-200 px-5 py-3 text-xs font-bold hover:border-black transition-colors"
                >
                  <Sparkles size={14} className="text-[#c63f3e]" /> How to prepare
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          data-testid="ticket-card"
          className="rounded-[26px] bg-[#141412] text-[#eae4da] p-7 sm:p-8 min-h-[300px] flex flex-col justify-between relative overflow-hidden shadow-[0_30px_60px_-24px_rgba(20,20,18,0.6)]"
        >
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#c63f3e]/10" />
          <TicketIcon size={30} className="text-[#c63f3e] relative" />
          <div className="relative">
            <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-[#eae4da]/45">
              Your ticket · Booking code
            </p>
            <p
              data-testid="booking-confirmation-code"
              className="font-mono font-bold text-3xl sm:text-[2.5rem] leading-tight tracking-[0.08em] text-[#c63f3e] mt-3 break-all"
            >
              {booking.booking_code}
            </p>
            <p className="text-[11px] text-[#eae4da]/50 font-medium mt-2.5">Show this code at the door</p>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {prepareOpen && prepareItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div data-testid="prepare-checklist" className="mt-5 rounded-[26px] bg-white border border-black/[0.06] p-6">
              <p className="font-display font-extrabold tracking-tight">{t("prepare_title")}</p>
              <ul className="mt-4 space-y-2.5">
                {prepareItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-neutral-600 font-medium">
                    <span className="w-5 h-5 rounded-full bg-[#c63f3e] flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={11} className="text-white" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
